import type { PrivacyNotice } from './types';

export const PRIVACY_NOTICE_TEACHERS: PrivacyNotice = {
  id: 'teacher',
  label: 'For grown-ups',
  // Source title from the .docx first line. Rendered as a heading inside
  // the article (the modal chrome already says "Privacy Notice").
  title: 'About our Privacy Notice',
  intro: [
    'This notice explains how Norhi Limited (“Norhi”) processes personal data relating to teachers and school staff who use the Norhi platform to facilitate our programmes. It should be read alongside the school’s own privacy notice, which covers how the school handles pupil and staff data more broadly.',
    'A separate student privacy notice is available for sharing with pupils and is published at www.norhi.io.',
  ],
  lastUpdated: '18 September 2026',
  version: '1.2',
  blocks: [
    { kind: 'heading', level: 2, text: '1. Who We Are' },
    {
      kind: 'paragraph',
      text: 'Norhi is a company registered in England and Wales (Company No. 17347313). We deliver structured enrichment programmes, amongst others EduCity, to schools in the United Kingdom.',
    },
    // Verbatim from the source: each label and value is a separate line
    // in the .docx, so we keep them as separate paragraphs rather than
    // collapsing them into a bullet with an inserted colon.
    { kind: 'paragraph', text: 'Controller' },
    { kind: 'paragraph', text: 'Norhi Limited, 9 Eastbourne Road, London, W4 3EB' },
    { kind: 'paragraph', text: 'Contact' },
    { kind: 'paragraph', text: 'enquiries@norhi.io' },

    { kind: 'heading', level: 2, text: '2. Personal Data We Hold About You' },
    {
      kind: 'paragraph',
      text: 'As a teacher or facilitator using the Norhi platform, we hold the following personal data about you:',
    },
    {
      kind: 'bullets',
      items: [
        'Your name',
        'Your school email address',
        'Your school and the year group(s) you facilitate',
        'Your activity on the Norhi platform',
      ],
    },
    {
      kind: 'paragraph',
      text: 'This data is received from your school via our Wonde MIS integration or directly from your school’s administration team. We do not collect any additional data about you beyond what is necessary to enable you to run the programme.',
    },

    { kind: 'heading', level: 2, text: '3. How We Use Your Data' },
    {
      kind: 'paragraph',
      text: 'We use your personal data for the following purposes only:',
    },
    {
      kind: 'bullets',
      items: [
        'To provision and manage your teacher account on the Norhi platform',
        'To enable you to log in via Google Single Sign-On (SSO) using your school Google account',
        'To enable you to monitor pupil progress, award virtual currency, and manage programme missions',
        'To send you support communications and programme updates relevant to your role as facilitator',
        'To enable Norhi to provide programme reporting to the school',
      ],
    },

    { kind: 'heading', level: 2, text: '4. Lawful Basis for Processing' },
    {
      kind: 'paragraph',
      text: 'We process your personal data on the following lawful bases under UK GDPR Article 6:',
    },
    {
      kind: 'table',
      headers: ['Data', 'Lawful Basis', 'Detail'],
      rows: [
        [
          'Name, email, school, role',
          'Contractual necessity',
          'Necessary to deliver the programme contracted by your school',
        ],
        [
          'Platform activity data',
          'Legitimate interests',
          'Necessary for Norhi to operate, support, and improve the platform; we have assessed this does not override your interests',
        ],
        [
          'Name and email (programme communications)',
          'Legitimate interests',
          'Necessary for Norhi to provide support and programme updates to the facilitating teacher; communications are limited to matters directly relevant to the programme',
        ],
      ],
    },

    { kind: 'heading', level: 2, text: '5. How You Log In' },
    {
      kind: 'paragraph',
      text: 'You log in to the Norhi platform using Google Single Sign-On (SSO) via your school Google account. In doing so, Google processes your authentication credentials on Norhi’s behalf as a data processor. Norhi does not receive or store your password.',
    },

    { kind: 'heading', level: 2, text: '6. Third Parties Who Process Your Data' },
    {
      kind: 'paragraph',
      text: 'We share your data with the following third parties only, each engaged under a data processing agreement:',
    },
    {
      kind: 'table',
      headers: ['Third Party', 'Role', 'Data Shared'],
      rows: [
        ['Wonde Ltd', 'MIS integration (school-authorised)', 'Name, email, year group'],
        [
          'Google LLC',
          'SSO authentication',
          'Authentication credentials only; no passwords stored by Norhi',
        ],
        [
          'Amazon Web Services (AWS) EMEA',
          'Cloud infrastructure (EEA)',
          'All platform data, stored within the EEA',
        ],
      ],
    },
    {
      kind: 'paragraph',
      text: 'We do not share your personal data with any advertising networks, data brokers, or any third party not listed above.',
    },

    { kind: 'heading', level: 2, text: '7. Your Role in Relation to Pupil Data' },
    {
      kind: 'paragraph',
      text: 'As a teacher using the Norhi platform, you will have access to pupil personal data including names, email addresses, and programme progress data. You are responsible for:',
    },
    {
      kind: 'bullets',
      items: [
        'Using pupil data only for the purposes of facilitating the Norhi programme',
        'Not sharing pupil login credentials or account data with unauthorised persons',
        'Directing any parental or pupil data rights requests to enquiries@norhi.io',
        'Reporting any suspected data security incident to your school’s Data Protection Officer or designated data protection lead, and to Norhi at enquiries@norhi.io, promptly and without undue delay',
      ],
    },
    {
      kind: 'paragraph',
      text: 'Your school acts as Data Controller for pupil data. Norhi acts as Data Processor. Any data rights requests from parents or pupils in respect of Norhi’s processing should be directed to enquiries@norhi.io.',
    },

    { kind: 'heading', level: 2, text: '8. The Parent Progress Report' },
    {
      kind: 'paragraph',
      text: 'At the end of the programme, pupils may choose to provide a parent or guardian email address within the Norhi app. If they do, Norhi sends a one-time progress report directly to that email address. This is the only direct communication Norhi sends to parents or guardians. All other parent communications relating to the programme should be managed by the school through its own channels.',
    },
    {
      kind: 'paragraph',
      text: 'Please inform parents of this feature as part of your standard programme communications. You may direct parents to the Norhi student privacy notice at www.norhi.io if they have questions about how their child’s data is used.',
    },

    { kind: 'heading', level: 2, text: '9. How Long We Keep Your Data' },
    {
      kind: 'table',
      headers: ['Data Type', 'Retention Period', 'Reason'],
      rows: [
        [
          'Teacher name, email, role',
          '12 months after programme end',
          'Operational necessity; no ongoing contractual relationship with individual teachers',
        ],
        [
          'Platform activity data (anonymised after programme end)',
          '6 years from programme end',
          'Programme reporting and service improvement',
        ],
        [
          'Parent email (progress report)',
          'Deleted within 30 days of dispatch',
          'Single-use communication; deleted promptly after sending',
        ],
      ],
    },

    { kind: 'heading', level: 2, text: '10. Data Security' },
    {
      kind: 'paragraph',
      text: 'Norhi maintains appropriate technical and organisational security measures to protect personal data against unauthorised access, loss, or disclosure, including:',
    },
    {
      kind: 'bullets',
      items: [
        'All data encrypted in transit and at rest',
        'Data stored on AWS infrastructure within the EEA',
        'Access to personal data restricted to authorised Norhi staff only',
        'All Norhi staff who attend schools in person hold a valid enhanced DBS certificate',
      ],
    },
    {
      kind: 'paragraph',
      text: 'Full details of Norhi’s data security practices are available on written request to enquiries@norhi.io.',
    },

    { kind: 'heading', level: 2, text: '11. Your Rights' },
    {
      kind: 'paragraph',
      text: 'Under the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018, you have the following rights in respect of your personal data:',
    },
    {
      kind: 'table',
      headers: ['Right', 'What It Means', 'How to Exercise'],
      rows: [
        ['Access', 'Request a copy of data we hold about you', 'Email enquiries@norhi.io'],
        ['Rectification', 'Ask us to correct inaccurate data', 'Email enquiries@norhi.io'],
        [
          'Erasure',
          'Ask us to delete your data in certain circumstances',
          'Email enquiries@norhi.io',
        ],
        [
          'Restriction',
          'Ask us to restrict processing in certain circumstances',
          'Email enquiries@norhi.io',
        ],
        [
          'Objection',
          'Object to processing based on legitimate interests',
          'Email enquiries@norhi.io',
        ],
        [
          'Portability',
          'Request your data in a structured, machine-readable format',
          'Email enquiries@norhi.io',
        ],
      ],
    },
    {
      kind: 'paragraph',
      text: 'We will respond to all requests within one calendar month. If you are not satisfied with our response, you have the right to lodge a complaint with the Information Commissioner’s Office (ICO) at ico.org.uk or by post to: ICO, Wycliffe House, Water Lane, Wilmslow, Cheshire, SK9 5AF.',
    },

    { kind: 'heading', level: 2, text: '12. Changes to This Notice' },
    {
      kind: 'paragraph',
      text: 'We may update this notice from time to time. We will publish the updated notice at www.norhi.io with a revised effective date and version number. Where changes are material, we will make reasonable efforts to notify the relevant school contact by email. Continued use of the Norhi platform following publication of an updated notice constitutes acceptance of the revised terms.',
    },
  ],
};
